import { Injectable, inject } from '@angular/core';
import { SideNavGroup, SideNavUser } from '@umss/shared/ui';
import { AuthService, AuthStorageService } from '@umss/shared/data-access';
import { API_BASE_URL } from '@umss/shared/core';
import { map, Observable } from 'rxjs';
import { ContractorContextService } from './contractor-context.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private auth = inject(AuthService);
  private authStorage = inject(AuthStorageService);
  private contractorContext = inject(ContractorContextService);
  private apiBaseUrl = inject(API_BASE_URL);

  /**
   * Get contractor navigation structure
   * Returns the side-nav groups configuration for the contractor app
   * Matches Figma design structure exactly
   */
  getContractorNavigation(): SideNavGroup[] {
    return [
      {
        label: '',
        items: [
          {
            label: 'Dashboard',
            value: 'dashboard',
            iconName: 'phosphorHouse',
            selected: false
          },
          {
            label: 'Notifications',
            value: 'notifications',
            iconName: 'phosphorBell',
            badge: 0,
            showBadge: false,
            selected: false
          },
          {
            label: 'Schedule',
            value: 'schedule',
            iconName: 'phosphorCalendarBlank',
            selected: false
          },
          {
            label: 'Job Postings',
            value: 'jobPostings',
            iconName: 'phosphorBriefcase',
            selected: false
          },
          {
            label: 'Find Shifts',
            value: 'findShifts',
            iconName: 'phosphorMagnifyingGlass',
            selected: false
          },
          {
            label: 'Pay',
            value: 'pay',
            iconName: 'phosphorCurrencyDollar',
            selected: false
          },
          {
            label: 'Facilities',
            value: 'facilities',
            iconName: 'phosphorBuildings',
            selected: false
          },
          {
            label: 'Availability',
            value: 'availability',
            iconName: 'phosphorCalendarCheck',
            selected: false
          },
          {
            label: 'Referrals',
            value: 'referrals',
            iconName: 'phosphorUserPlus',
            selected: false
          },
          // {
          //   label: 'Support',
          //   value: 'support',
          //   iconName: 'phosphorQuestion',
          //   selected: false
          // },
          {
            label: 'User',
            value: 'user',
            iconName: '',
            selected: false
          },
          {
            label: 'Settings',
            value: 'settings',
            iconName: 'phosphorSlidersHorizontal',
            selected: false
          },
          {
            label: 'Sign Out',
            value: 'signOut',
            iconName: 'phosphorSignOut',
            selected: false
          }
        ]
      }
    ];
  }

  /**
   * Get current user information for side-nav display
   * Integrates with AuthService to get real user data
   * Returns Observable to support async user data loading
   */
  getCurrentUser(): SideNavUser | undefined {
    const authState = this.authStorage.state;

    if (!authState.lastSessionAt || !authState.user) {
      return undefined;
    }

    const user = authState.user;

    return {
      name: `${user.firstName} ${user.lastName}`,
      role: this.formatRole(authState.role),
      avatarSrc: this.getProfilePictureUrl(),
      initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    };
  }

  /**
   * Get current user as Observable
   * Useful for reactive updates when auth state changes
   */
  getCurrentUser$(): Observable<SideNavUser | undefined> {
    return this.authStorage.state$.pipe(
      map((authState) => {
        if (!authState.lastSessionAt || !authState.user) {
          return undefined;
        }

        const user = authState.user;

        return {
          name: `${user.firstName} ${user.lastName}`,
          role: this.formatRole(authState.role),
          avatarSrc: this.getProfilePictureUrl(),
          initials: `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        };
      })
    );
  }

  private getProfilePictureUrl(): string | undefined {
    const profilePictureUrl =
      this.contractorContext.contractor()?.profilePictureUrl;
    if (!profilePictureUrl) return undefined;

    if (/^https?:\/\//i.test(profilePictureUrl)) {
      return profilePictureUrl;
    }

    const relativeUrl = profilePictureUrl.replace(/^\/+/, '');
    return `${this.apiBaseUrl}/${relativeUrl}`;
  }

  /**
   * Get route path for a navigation item value
   * Maps nav item values to actual routes
   */
  getRouteForValue(value: string): string {
    const routeMap: Record<string, string> = {
      dashboard: '/dashboard',
      notifications: '/notifications',
      schedule: '/schedule',
      jobPostings: '/job-postings',
      findShifts: '/find-shifts',
      pay: '/pay',
      facilities: '/facilities',
      availability: '/availability',
      referrals: '/referrals',
      support: '/support',
      user: '/profile',
      settings: '/settings',
      signOut: '/auth/login'
    };

    return routeMap[value] || '/dashboard';
  }

  /**
   * Update selected nav item based on current route
   * Useful for highlighting active navigation item
   */
  updateSelectedItem(
    currentRoute: string,
    groups: SideNavGroup[]
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        selected: this.getRouteForValue(item.value) === currentRoute
      }))
    }));
  }

  /**
   * Update badge count for a specific nav item
   * Useful for notifications, pending items, etc.
   */
  updateBadgeCount(
    groups: SideNavGroup[],
    itemValue: string,
    count: number
  ): SideNavGroup[] {
    return groups.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.value === itemValue) {
          return {
            ...item,
            badge: count,
            showBadge: count > 0
          };
        }
        return item;
      })
    }));
  }

  /**
   * Get navigation filtered by user role/permissions
   * Can be extended to hide nav items based on user permissions
   */
  getNavigationByRole(): SideNavGroup[] {
    const allNavigation = this.getContractorNavigation();

    // For now, return all navigation
    // Can be extended to filter based on role:
    // if (authState.role === 'Contractor') {
    //   return allNavigation; // Show all
    // } else if (authState.role === 'ShiftAdministrator') {
    //   return filterNavigation(allNavigation, ['dashboard', 'scheduling']);
    // }

    return allNavigation;
  }

  /**
   * Format user role for display
   * Converts role enum to human-readable format
   */
  private formatRole(role: string | null): string {
    if (!role) return 'User';

    // Convert camelCase/PascalCase to Title Case with spaces
    return role
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase());
  }
}
